import { Button } from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { VENDOR_AGGREGATION_GROUP } from "@dransay/api/models";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NonTopPharmacyBanner } from "~/features/products/pharmacy-picker/non-top-pharmacy-banner.component";
import type {
	PharmacyPickerGroupShortcut,
	PharmacyPickerItem,
	PharmacyPickerNoResultsRecovery,
} from "~/features/products/pharmacy-picker/pharmacy-picker.types";
import {
	nonTopPharmacyToggleButtonClassName,
	nonTopPharmacyToggleWrapperClassName,
} from "~/features/products/pharmacy-picker/pharmacy-picker.utils";
import { PharmacyPickerAvailableCount } from "~/features/products/pharmacy-picker/pharmacy-picker-available-count.component";
import type { PharmacyPickerCardBadgeLayout } from "~/features/products/pharmacy-picker/pharmacy-picker-card.component";
import { PharmacyPickerCard } from "~/features/products/pharmacy-picker/pharmacy-picker-card.component";
import { PharmacyPickerGroupButton } from "~/features/products/pharmacy-picker/pharmacy-picker-group-button.component";
import { PharmacyPickerNoResults } from "~/features/products/pharmacy-picker/pharmacy-picker-no-results.component";
import { PharmacyWarningBanner } from "~/features/products/pharmacy-picker/pharmacy-warning-banner.component";
import { TopPharmacySection } from "~/features/products/pharmacy-picker/top-pharmacy-section.component";
import { WhyTopPharmacyCollapsible } from "~/features/products/pharmacy-picker/why-top-pharmacy-collapsible.component";

export type PharmacyPickerListProps = {
	pharmacies: PharmacyPickerItem[];
	selectedId?: string;
	onSelect: (id: string) => void;
	showPrice?: boolean;
	showDistance?: boolean;
	searchQuery?: string;
	groupShortcuts?: PharmacyPickerGroupShortcut[];
	onSelectGroup?: (groupId: string) => void;
	onSwitchGroup?: (groupId: string) => void;
	onExpandNonTop?: () => void;
	activeGroupId?: string;
	nonFeaturedCount?: number;
	/**
	 * The unscoped "of y" total for the available-count line. Radius surfaces
	 * scope the group-shortcut counts to the visible set (Figma 4662:59239), so
	 * the global total has to arrive on its own channel.
	 */
	availableTotal?: number;
	bannerBreakoutClassName?: string;
	product?: ProductNoEdibleInfo;
	className?: string;
	badgeLayout?: PharmacyPickerCardBadgeLayout;
	/**
	 * When true the "no results" message is rendered by the caller (in the white
	 * search header) instead of inside the scrollable list.
	 */
	noResultsMessageInHeader?: boolean;
	/**
	 * Map delivery types (courier/pickup) are location-based: when no top
	 * pharmacies are nearby, show the location/courier warning instead of the
	 * product-specific "none offer this product" message.
	 */
	isMapDelivery?: boolean;
	hideWhyTopCollapsible?: boolean;
	noResultsRecovery?: PharmacyPickerNoResultsRecovery;
	/**
	 * Whether this surface separates Top from non-Top pharmacies. Shipping does;
	 * courier and Abholung show one flat list of every pharmacy, with no Top
	 * section, no "show/hide non-top" toggle and no non-top banner.
	 */
	showTopSplit?: boolean;
};

function PharmacyCards({
	pharmacies,
	selectedId,
	onSelect,
	showPrice,
	showDistance,
	product,
	searchQuery,
	badgeLayout,
}: {
	pharmacies: PharmacyPickerItem[];
	selectedId?: string;
	onSelect: (id: string) => void;
	showPrice: boolean;
	showDistance: boolean;
	product?: ProductNoEdibleInfo;
	searchQuery: string;
	badgeLayout: PharmacyPickerCardBadgeLayout;
}) {
	return (
		<div className="flex flex-col gap-2">
			{pharmacies.map((pharmacy) => (
				<PharmacyPickerCard
					key={pharmacy.id}
					item={pharmacy}
					isSelected={selectedId === pharmacy.id}
					onSelect={() => onSelect(pharmacy.id)}
					showPrice={showPrice}
					showDistance={showDistance}
					product={product}
					searchQuery={searchQuery}
					badgeLayout={badgeLayout}
				/>
			))}
		</div>
	);
}

function EmptyFeaturedWarning({
	isMapDelivery,
	hasSearch,
}: {
	isMapDelivery: boolean;
	hasSearch: boolean;
}) {
	const { t } = useTranslation("product");
	const isNearbyWarning = isMapDelivery && !hasSearch;
	return (
		<PharmacyWarningBanner
			data-testid={
				isNearbyWarning
					? "pharmacy-picker-no-top-nearby"
					: "pharmacy-picker-no-top"
			}
		>
			{isNearbyWarning
				? t("vendor.picker.noTopNearby")
				: t("vendor.pdp.topPharmaciesNoResults")}
		</PharmacyWarningBanner>
	);
}

function AllPharmaciesSectionHeader({
	hasSearch,
	showGroupButtons,
	allGroup,
	groupShortcuts,
	nonFeaturedLength,
	activeGroupId,
	onSwitchGroup,
}: {
	hasSearch: boolean;
	showGroupButtons: boolean;
	allGroup?: PharmacyPickerGroupShortcut;
	groupShortcuts: PharmacyPickerGroupShortcut[];
	nonFeaturedLength: number;
	activeGroupId?: string;
	onSwitchGroup?: (groupId: string) => void;
}) {
	const { t } = useTranslation("product");
	if (hasSearch) {
		return null;
	}
	if (showGroupButtons && allGroup) {
		return (
			<PharmacyPickerGroupButton
				group={allGroup}
				isActive={activeGroupId === allGroup.id}
				onClick={() => onSwitchGroup?.(allGroup.id)}
			/>
		);
	}
	if (groupShortcuts.length === 0) {
		return null;
	}
	return (
		<div className="max-md:text-body-sm md:text-body text-gray-700 border border-gray-200 rounded-lg px-4 py-3 bg-white">
			{t("vendor.picker.allPharmaciesSection")}{" "}
			<span className="text-gray-500">({nonFeaturedLength})</span>
		</div>
	);
}

export function PharmacyPickerList({
	pharmacies,
	selectedId,
	onSelect,
	showPrice = false,
	showDistance = false,
	searchQuery = "",
	groupShortcuts = [],
	onSelectGroup,
	onSwitchGroup,
	onExpandNonTop,
	activeGroupId,
	nonFeaturedCount = 0,
	availableTotal,
	bannerBreakoutClassName = "-mx-5 px-5 md:-mx-11 md:px-11",
	product,
	className,
	badgeLayout = "listing",
	noResultsMessageInHeader = false,
	isMapDelivery = false,
	hideWhyTopCollapsible = false,
	noResultsRecovery,
	showTopSplit = true,
}: PharmacyPickerListProps) {
	const { t } = useTranslation("product");
	const [showNonTop, setShowNonTop] = useState(false);
	const [manuallyHidden, setManuallyHidden] = useState(false);
	const { featured, nonFeatured } = useMemo(() => {
		const featuredItems = pharmacies.filter((p) => p.featured);
		const nonFeaturedItems = pharmacies.filter((p) => !p.featured);
		return { featured: featuredItems, nonFeatured: nonFeaturedItems };
	}, [pharmacies]);
	const normalizedSearch = searchQuery.trim();
	const hasSearch = normalizedSearch.length > 0;

	useEffect(() => {
		if (normalizedSearch) {
			setManuallyHidden(false);
		}
	}, [normalizedSearch]);

	useEffect(() => {
		if (normalizedSearch) {
			return;
		}
		if (activeGroupId === VENDOR_AGGREGATION_GROUP.TOP) {
			setShowNonTop(false);
			setManuallyHidden(true);
		} else if (activeGroupId === VENDOR_AGGREGATION_GROUP.ALL) {
			setShowNonTop(true);
			setManuallyHidden(false);
		}
	}, [activeGroupId, normalizedSearch]);

	const finalShowNonTop =
		(hasSearch && nonFeatured.length > 0 && !manuallyHidden) || showNonTop;
	const noSearchResults = hasSearch && pharmacies.length === 0;
	if (noSearchResults && onSelectGroup && groupShortcuts.length > 0) {
		return (
			<PharmacyPickerNoResults
				groupShortcuts={groupShortcuts}
				onSelectGroup={onSelectGroup}
				showMessage={!noResultsMessageInHeader}
				hideWhyTopCollapsible={hideWhyTopCollapsible}
				recovery={noResultsRecovery}
			/>
		);
	}
	const topGroup = groupShortcuts.find((g) => g.id === "top");
	const allGroup = groupShortcuts.find((g) => g.id === "all");
	if (!showTopSplit) {
		return (
			<div
				className={className ?? "flex flex-col gap-3 px-4 pb-4"}
				data-testid="pharmacy-picker-list"
			>
				{hideWhyTopCollapsible ? (
					<PharmacyPickerAvailableCount
						count={pharmacies.length}
						total={availableTotal ?? allGroup?.count ?? pharmacies.length}
					/>
				) : null}
				{!hasSearch && allGroup && onSwitchGroup ? (
					<PharmacyPickerGroupButton
						group={allGroup}
						isActive={activeGroupId === allGroup.id}
						onClick={() => onSwitchGroup(allGroup.id)}
					/>
				) : null}
				<PharmacyCards
					pharmacies={pharmacies}
					selectedId={selectedId}
					onSelect={onSelect}
					showPrice={showPrice}
					showDistance={showDistance}
					product={product}
					searchQuery={searchQuery}
					badgeLayout={badgeLayout}
				/>
			</div>
		);
	}
	// Clickable Top/All group switching requires both aggregation groups in shortcuts.
	// Pickup/courier now expose TOP + ALL when enableTopPharmacyCartPromotion is enabled.
	const showGroupButtons = !!onSwitchGroup && !!topGroup && !!allGroup;
	// While searching, ignore global nonFeaturedCount — it would show the toggle
	// with no non-top matches behind it.
	const hasNonTop =
		nonFeatured.length > 0 || (!hasSearch && nonFeaturedCount > 0);
	const availableTotalResolved =
		availableTotal ?? allGroup?.count ?? pharmacies.length;
	return (
		<div
			className={className ?? "flex flex-col gap-3 px-4 pb-4"}
			data-testid="pharmacy-picker-list"
		>
			{hideWhyTopCollapsible ? (
				// Counts what this list actually shows. The loaded page holds every
				// pharmacy so search can reach the non-top ones, so while the non-top
				// section is collapsed the page total would overstate the list.
				<PharmacyPickerAvailableCount
					count={finalShowNonTop ? pharmacies.length : featured.length}
					total={availableTotalResolved}
				/>
			) : (
				!hasSearch && (
					<WhyTopPharmacyCollapsible
						leftContent={<TopPharmacySection count={featured.length} />}
					/>
				)
			)}
			{!hasSearch && showGroupButtons && topGroup && (
				<PharmacyPickerGroupButton
					group={topGroup}
					isActive={activeGroupId === topGroup.id}
					onClick={() => {
						setShowNonTop(false);
						setManuallyHidden(true);
						onSwitchGroup?.(topGroup.id);
					}}
				/>
			)}
			{featured.length === 0 && (
				<EmptyFeaturedWarning
					isMapDelivery={isMapDelivery}
					hasSearch={hasSearch}
				/>
			)}
			<PharmacyCards
				pharmacies={featured}
				selectedId={selectedId}
				onSelect={onSelect}
				showPrice={showPrice}
				showDistance={showDistance}
				product={product}
				searchQuery={searchQuery}
				badgeLayout={badgeLayout}
			/>
			{hasNonTop && !finalShowNonTop && (
				<div className={nonTopPharmacyToggleWrapperClassName}>
					<Button
						type="button"
						variant="ghost"
						size={null}
						className={`h-auto px-0 ${nonTopPharmacyToggleButtonClassName}`}
						onClick={() => {
							setShowNonTop(true);
							setManuallyHidden(false);
							onExpandNonTop?.();
							onSwitchGroup?.(VENDOR_AGGREGATION_GROUP.ALL);
						}}
						data-testid="show-non-top-pharmacies"
					>
						{t("vendor.picker.showNonTop")}
					</Button>
				</div>
			)}
			{finalShowNonTop && (showGroupButtons || nonFeatured.length > 0) && (
				<>
					<NonTopPharmacyBanner
						onHide={() => {
							setShowNonTop(false);
							setManuallyHidden(true);
							onSwitchGroup?.(VENDOR_AGGREGATION_GROUP.TOP);
						}}
						breakoutClassName={bannerBreakoutClassName}
						showExpertWarning={!hideWhyTopCollapsible}
					/>
					<AllPharmaciesSectionHeader
						hasSearch={hasSearch}
						showGroupButtons={showGroupButtons}
						allGroup={allGroup}
						groupShortcuts={groupShortcuts}
						nonFeaturedLength={nonFeatured.length}
						activeGroupId={activeGroupId}
						onSwitchGroup={onSwitchGroup}
					/>
					<PharmacyCards
						pharmacies={nonFeatured}
						selectedId={selectedId}
						onSelect={onSelect}
						showPrice={showPrice}
						showDistance={showDistance}
						product={product}
						searchQuery={searchQuery}
						badgeLayout={badgeLayout}
					/>
				</>
			)}
		</div>
	);
}
